import {
    ApolloError,
    type QueryHookOptions,
    type TypedDocumentNode,
    useQuery,
} from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { type ComponentType, type ReactNode, Suspense, useEffect } from 'react';

import { Box } from '@myphx-engineering/phx-design-system';

import { Loader } from '../components/common/Loader';
import { useLoading } from '../hooks/use-loading';
import RootErrorRoute from '../routes/error/RootErrorRoute';
import type { RequiredQueryVariables, ResultOf } from '../types';

export type ExtractDocumentNodeDataValue<Type> =
    Type extends TypedDocumentNode<infer X, infer _Y> ? X : never;
export type ExtractDocumentNodeVariableValue<Type> =
    Type extends TypedDocumentNode<infer _X, infer Y> ? Y : never;

export type LoadingIndicatorProps<Q extends DocumentNode> = {
    query: Q;
    loadingPlaceholderComponent?: ReactNode;
    renderError?: (error: ApolloError) => ReactNode | null;
    errorComponent?: ReactNode;
    queryOptions?: Partial<Omit<QueryHookOptions, 'variables'>>;
} & RequiredQueryVariables<Q> &
    (
        | {
              component: ComponentType<{ data: ResultOf<Q> }>;
              render?: undefined;
          }
        | {
              render: (data: ResultOf<Q>) => ReactNode | null;
              component?: undefined;
          }
    );

export type DataHandlerType<Q extends DocumentNode, OtherProps = object> = (
    props: {
        data: ResultOf<Q>;
    } & OtherProps
) => ReactNode;

export const QueryLoader = <Q extends DocumentNode>(
    props: LoadingIndicatorProps<Q>
) => {
    const queryOptions: QueryHookOptions | undefined =
        'variables' in props
            ? ({ variables: props.variables } as QueryHookOptions)
            : undefined;

    const {
        query,
        component: Child,
        render,
        errorComponent,
        loadingPlaceholderComponent,
        renderError,
    } = props;

    const { setIsLoading } = useLoading();

    const { data, loading, error } = useQuery(query, {
        ...queryOptions,
        ...props.queryOptions,
        onCompleted: (data) => {
            setIsLoading(false);
            props.queryOptions?.onCompleted?.(data);
        },
    });

    useEffect(() => {
        if (loading) {
            setIsLoading(true);
        }

        return () => {
            setIsLoading(false);
        };
    }, [loading, setIsLoading]);

    if (loading) {
        return loadingPlaceholderComponent || <Loader />;
    }

    //TODO: better error handling is needed. We probably should get rid of the query loader entirely.
    // If error policy not set to 'all' or 'ignore' OR we passed an error handler
    if (
        (error && props.queryOptions?.errorPolicy === 'none') ||
        (error && (errorComponent || renderError))
    ) {
        // oxlint-disable-next-line no-console
        console.error(
            `threw an error when query ${JSON.stringify(query)} resolved.`
        );

        return (
            renderError?.(error) ??
            errorComponent ?? (
                <Box p="md">
                    <RootErrorRoute />
                </Box>
            )
        );
    }

    return (
        <Suspense>
            {Child && <Child data={data} />}
            {render?.(data)}
        </Suspense>
    );
};
